import { Field, Form, FormRenderProps } from 'react-final-form'
import React from 'react'
import startCase from 'lodash/startCase'

import {
  Box,
  FormModal,
  FormSectionTitle,
  OptionType,
  SpinnerButton,
  TextField,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'

import {
  CancelOpportunityMutationOptions,
  CancelOpportunityMutationVariables,
  OpportunityCancellationType,
  PricingDetailsFormInput,
  TradeIntentType,
  useCancelOpportunityMutation,
} from 'graphql/bindings/AdminBindings'
import { cancellationReasonOptions, competitorOptions, opportunityCancellationTypeOptions } from 'util/Enums'
import { formatPriceOrValuation, formatSharesOrSize } from 'util/ParsersFormatters'
import { parseSizeOrPrice, validatePriceOrValuation, validateSizeOrSharesField } from 'util/FormUtils'

type FormShape = {
  cancellation_type: OpportunityCancellationType
  reason?: string
  notes?: string
  competitor?: string
  lost_transaction_pricing?: {
    size_or_shares?: string
    price_or_valuation?: string
  }
}

type FormProps = FormRenderProps<FormShape> & {
  loading: boolean
  opportunityType: TradeIntentType
  issuerName: string
}

const CancellationForm: React.FC<FormProps> = ({
  handleSubmit,
  loading,
  values,
  form,
  opportunityType,
  issuerName,
  valid,
}) => {
  const showReason = [OpportunityCancellationType.LostToCompetitor, OpportunityCancellationType.FellThrough].includes(
    values.cancellation_type
  )
  const showCompetitor = values.cancellation_type === OpportunityCancellationType.LostToCompetitor
  const showPricing = values.cancellation_type === OpportunityCancellationType.LostToCompetitor
  const showNotes = [
    OpportunityCancellationType.FellThrough,
    OpportunityCancellationType.NoIntent,
    OpportunityCancellationType.Closed,
  ].includes(values.cancellation_type)
  const reasonOptions = cancellationReasonOptions[values.cancellation_type] ?? []

  return (
    <form onSubmit={handleSubmit}>
      <ValueAutocompleteField
        name="cancellation_type"
        label="Why cancel this opportunity?"
        options={opportunityCancellationTypeOptions}
        onChange={val => {
          const cancellation_type = val ? (val as OptionType<OpportunityCancellationType>).value : null
          form.reset({ cancellation_type })
        }}
      />
      {showReason && <ValueAutocompleteField name="reason" label="What is the reason?" options={reasonOptions} />}
      {showCompetitor && (
        <ValueAutocompleteField name="competitor" label="Which competitor?" options={competitorOptions} />
      )}
      {showNotes && <Field name="notes" component={TextField} label="Any additional details?" fullWidth multiline />}
      {showPricing && (
        <Box>
          <FormSectionTitle>
            How much did they {opportunityType === TradeIntentType.Bid ? 'buy' : 'sell'}?
          </FormSectionTitle>
          <Box>{issuerName}</Box>
          <Field
            name="lost_transaction_pricing.size_or_shares"
            label="Size/Shares"
            component={TextField}
            format={formatSharesOrSize}
            formatOnBlur
          />
          <Field
            name="lost_transaction_pricing.price_or_valuation"
            label="Price/Valuation"
            component={TextField}
            format={formatPriceOrValuation}
            formatOnBlur
          />
        </Box>
      )}
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={loading}
          caption="Cancel Opportunity"
          disabled={!valid}
        />
      </Box>
    </form>
  )
}

type ModalProps = {
  hideModal: () => void
  opportunityId: string
  opportunityType: TradeIntentType
  issuerName: string
  refetchQueries?: CancelOpportunityMutationOptions['refetchQueries']
}

export const CancellationModal: React.FC<ModalProps> = ({
  hideModal,
  opportunityId,
  opportunityType,
  issuerName,
  refetchQueries,
}) => {
  const [cancelOpportunity, { loading }] = useCancelOpportunityMutation({ refetchQueries })

  const handleSubmit = (values: FormShape) => {
    const { lost_transaction_pricing, ...cancellationForm } = values
    const { size_or_shares, price_or_valuation } = lost_transaction_pricing || {}

    const parsedSizeValues = size_or_shares ? parseSizeOrPrice(size_or_shares, 'size', undefined) : {}

    const parsedPriceValues = price_or_valuation ? parseSizeOrPrice(price_or_valuation, 'price', undefined) : {}

    const lostTransactionPricingForm: PricingDetailsFormInput = Object.assign({}, parsedSizeValues, parsedPriceValues)

    const variables: CancelOpportunityMutationVariables = { id: opportunityId, cancellationForm }

    if (Object.keys(lostTransactionPricingForm).length > 0) {
      variables.cancellationForm.lost_transaction_pricing = lostTransactionPricingForm
    }

    cancelOpportunity({ variables }).then(() => hideModal())
  }

  const validate = (values: FormShape) => {
    const { lost_transaction_pricing } = values
    const { size_or_shares, price_or_valuation } = lost_transaction_pricing || {}

    const requiredFields = ['cancellation_type'] as string[]
    const valueObj = values as { [key: string]: any }

    const errors = {
      lost_transaction_pricing: {
        size_or_shares: validateSizeOrSharesField(size_or_shares),
        price_or_valuation: validatePriceOrValuation(price_or_valuation, opportunityType),
      },
    }

    return requiredFields.reduce((acc, el) => {
      if (!valueObj[el]) {
        return { ...acc, [el]: `${startCase(el)} is required` }
      }
      return acc
    }, errors)
  }

  return (
    <FormModal open hideModal={hideModal} title="Cancel Opportunity">
      <Form<FormShape>
        onSubmit={handleSubmit}
        initialValues={{ cancellation_type: OpportunityCancellationType.LostToCompetitor }}
        validate={validate}>
        {formProps => (
          <CancellationForm
            loading={loading}
            opportunityType={opportunityType}
            issuerName={issuerName}
            {...formProps}
          />
        )}
      </Form>
    </FormModal>
  )
}
